import { normalizeIncomingMessage, REALTIME_EVENT_TYPES } from './types';
import { getRealtimeWsUrl, isRealtimeWsEnabled } from './config';

const DEFAULT_MAX_BACKOFF_MS = 30000;
const DEFAULT_MAX_PROCESSED = 500;

/**
 * Gateway identity resolution keeps a single value per query key. Extra orgs
 * must use buyer_organization_ulid / org_ulid, or a broker acting for a buyer
 * never receives rfq.bid.expired.
 * @param {URLSearchParams} params
 * @param {{ organizationUlid?: string, userUlid?: string, extraOrgUlids?: string[] }} [identity]
 */
export function appendRealtimeIdentityParams(params, identity = {}) {
  if (identity.organizationUlid) params.set('organization_ulid', identity.organizationUlid);
  if (identity.userUlid) params.set('user_ulid', identity.userUlid);
  const extras = [];
  (identity.extraOrgUlids || []).forEach((org) => {
    const value = typeof org === 'string' ? org.trim() : '';
    if (!value || value === identity.organizationUlid || extras.includes(value)) return;
    extras.push(value);
  });
  if (extras[0]) params.set('buyer_organization_ulid', extras[0]);
  if (extras[1]) params.set('org_ulid', extras[1]);
  return params;
}

/**
 * Production WebSocket client: reconnect, token refresh, queue, dedupe, sequence guards.
 * Intentional model: **one socket per browser tab** (singleton). Handlers multiplex over it.
 */
export class RealtimeService {
  /**
   * @param {Object} options
   * @param {() => Promise<string|null|undefined>|string|null|undefined} options.getAccessToken
   * @param {() => ({ organizationUlid?: string, userUlid?: string, extraOrgUlids?: string[] })} [options.getIdentity]
   * @param {string} [options.wsUrl]
   * @param {boolean} [options.enabled]
   * @param {(reason: string) => void} [options.onSessionExpired]
   * @param {(envelope: import('./types').RealtimeEnvelope) => void} [options.onEvent]
   */
  constructor(options) {
    this.applyOptions(options);

    /** @type {WebSocket|null} */
    this.socket = null;
    /** @type {import('./types').RealtimeStatus} */
    this.status = 'idle';
    this.reconnectAttempt = 0;
    this.shouldReconnect = false;
    this.reconnectTimer = null;
    /** @type {Promise<void>|null} */
    this.connectInFlight = null;
    /** @type {Array<Record<string, unknown>>} */
    this.outboundQueue = [];
    /** @type {Map<string, Set<import('./types').RealtimeHandler>>} */
    this.handlers = new Map();
    /** @type {Set<string>} */
    this.processedIds = new Set();
    /** @type {Map<string, number>} */
    this.lastSequenceByOrg = new Map();
    /** @type {Set<() => void>} */
    this.statusListeners = new Set();
    this.subscribedChannels = new Set();
  }

  /**
   * Update callbacks/config without tearing down an open socket.
   * @param {ConstructorParameters<typeof RealtimeService>[0]} options
   */
  applyOptions(options) {
    if (!options) return;
    if (options.getAccessToken) this.getAccessToken = options.getAccessToken;
    if (options.getIdentity) this.getIdentity = options.getIdentity;
    if (options.wsUrl) this.wsUrl = options.wsUrl;
    if (typeof options.enabled === 'boolean') this.enabled = options.enabled;
    else if (this.enabled === undefined) this.enabled = isRealtimeWsEnabled();
    if (options.onSessionExpired) this.onSessionExpired = options.onSessionExpired;
    if (options.onEvent) this.onEvent = options.onEvent;
    if (!this.wsUrl) this.wsUrl = getRealtimeWsUrl();
  }

  isEnabled() {
    return Boolean(this.enabled);
  }

  isSocketActive() {
    return Boolean(
      this.socket &&
        (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)
    );
  }

  /**
   * @param {import('./types').RealtimeStatus} status
   */
  setStatus(status) {
    this.status = status;
    this.statusListeners.forEach((fn) => {
      try {
        fn();
      } catch {
        // ignore
      }
    });
  }

  /**
   * @param {() => void} listener
   */
  onStatusChange(listener) {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  /**
   * @param {string|string[]} eventType
   * @param {import('./types').RealtimeHandler} handler
   */
  subscribe(eventType, handler) {
    const types = Array.isArray(eventType) ? eventType : [eventType];
    types.forEach((type) => {
      if (!this.handlers.has(type)) this.handlers.set(type, new Set());
      this.handlers.get(type).add(handler);
    });
    return () => {
      types.forEach((type) => this.handlers.get(type)?.delete(handler));
    };
  }

  /**
   * @param {string[]} channels
   */
  subscribeChannels(channels) {
    channels.forEach((c) => this.subscribedChannels.add(c));
    this.send({ action: 'subscribe', channels });
  }

  async connect() {
    if (!this.enabled) return;
    if (this.isSocketActive()) return;
    if (this.connectInFlight) return this.connectInFlight;

    this.shouldReconnect = true;
    this.connectInFlight = this._openSocket().finally(() => {
      this.connectInFlight = null;
    });
    return this.connectInFlight;
  }

  async _openSocket() {
    // Re-check after awaiting token — another caller may have opened meanwhile
    if (this.isSocketActive()) return;

    this.setStatus(this.reconnectAttempt > 0 ? 'reconnecting' : 'connecting');

    let token;
    try {
      token = await this.getAccessToken();
    } catch {
      token = null;
    }
    if (!token) {
      this.scheduleReconnect();
      return;
    }
    if (this.isSocketActive()) return;

    const identity = this.getIdentity() || {};
    const params = new URLSearchParams();
    params.set('access_token', token);
    appendRealtimeIdentityParams(params, identity);

    const base = this.wsUrl.replace(/\/$/, '');
    const fullUrl = `${base}${base.includes('?') ? '&' : '?'}${params.toString()}`;

    try {
      const socket = new WebSocket(fullUrl);
      this.socket = socket;

      socket.onopen = () => {
        if (this.socket !== socket) return;
        this.reconnectAttempt = 0;
        this.setStatus('open');
        this.flushQueue();
        if (this.subscribedChannels.size > 0) {
          this.send({ action: 'subscribe', channels: Array.from(this.subscribedChannels) });
        }
      };

      socket.onmessage = (event) => {
        if (this.socket !== socket) return;
        this.handleRawMessage(event.data);
      };

      socket.onerror = () => {
        // onclose handles reconnect
      };

      socket.onclose = () => {
        if (this.socket !== socket) return;
        this.setStatus('closed');
        this.socket = null;
        if (this.shouldReconnect) this.scheduleReconnect();
      };
    } catch {
      this.scheduleReconnect();
    }
  }

  disconnect() {
    this.shouldReconnect = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      try {
        this.socket.close(1000, 'client disconnect');
      } catch {
        // ignore
      }
      this.socket = null;
    }
    this.setStatus('closed');
  }

  scheduleReconnect() {
    if (!this.shouldReconnect) return;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectAttempt += 1;
    const exp = Math.min(1000 * 2 ** Math.min(this.reconnectAttempt, 5), DEFAULT_MAX_BACKOFF_MS);
    const jitter = Math.floor(Math.random() * 400);
    this.reconnectTimer = setTimeout(() => this.connect(), exp + jitter);
  }

  /**
   * @param {Record<string, unknown>} message
   */
  send(message) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      this.outboundQueue.push(message);
      return false;
    }
    try {
      this.socket.send(JSON.stringify(message));
      return true;
    } catch {
      this.outboundQueue.push(message);
      return false;
    }
  }

  flushQueue() {
    const queued = [...this.outboundQueue];
    this.outboundQueue = [];
    queued.forEach((msg) => this.send(msg));
  }

  handleRawMessage(data) {
    let parsed;
    try {
      parsed = JSON.parse(data);
    } catch {
      return;
    }

    const envelope = normalizeIncomingMessage(parsed);
    if (!envelope) return;

    if (envelope.eventType === REALTIME_EVENT_TYPES.SESSION_EXPIRED) {
      this.shouldReconnect = false;
      this.onSessionExpired?.(String(envelope.payload?.reason || 'session.expired'));
      this.disconnect();
      return;
    }

    if (this.processedIds.has(envelope.correlationId)) return;
    this.processedIds.add(envelope.correlationId);
    if (this.processedIds.size > DEFAULT_MAX_PROCESSED) {
      const first = this.processedIds.values().next().value;
      this.processedIds.delete(first);
    }

    const org =
      /** @type {string|undefined} */ (envelope.payload?.organizationUlid) ||
      /** @type {string|undefined} */ (envelope.payload?.organization_ulid) ||
      /** @type {string|undefined} */ (envelope.payload?.notificationTo);
    if (org) {
      const last = this.lastSequenceByOrg.get(org) || 0;
      if (envelope.sequence < last - 1000) {
        return;
      }
      if (envelope.sequence > last) this.lastSequenceByOrg.set(org, envelope.sequence);
    }

    this.emit(envelope);
  }

  /**
   * @param {import('./types').RealtimeEnvelope} envelope
   */
  emit(envelope) {
    this.onEvent?.(envelope);
    const set = this.handlers.get(envelope.eventType);
    if (set) {
      set.forEach((handler) => {
        try {
          handler(envelope);
        } catch {
          // isolate handler errors
        }
      });
    }
    const star = this.handlers.get('*');
    if (star) {
      star.forEach((handler) => {
        try {
          handler(envelope);
        } catch {
          // ignore
        }
      });
    }

    // Bridge to OpenKnots / other MF listeners — do not open a second socket
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('129knots:realtime', { detail: envelope }));
    }
  }
}

/** @type {RealtimeService|null} */
let singleton = null;
/** @type {number} */
let singletonRefCount = 0;

/**
 * Reuse a single RealtimeService per tab. Does not disconnect an existing open socket
 * when options are refreshed (avoids reconnect storms from React re-renders).
 * @param {ConstructorParameters<typeof RealtimeService>[0]} options
 */
export function ensureRealtimeService(options) {
  if (!singleton) {
    singleton = new RealtimeService(options || {});
  } else {
    singleton.applyOptions(options || {});
  }
  return singleton;
}

/**
 * @deprecated Prefer ensureRealtimeService — kept for callers that expect create*
 * @param {ConstructorParameters<typeof RealtimeService>[0]} options
 */
export function createRealtimeService(options) {
  return ensureRealtimeService(options);
}

export function getRealtimeService() {
  return singleton;
}

/** Provider mount/unmount ref-count so StrictMode double-invoke does not leak sockets. */
export function acquireRealtimeService(options) {
  singletonRefCount += 1;
  return ensureRealtimeService(options);
}

export function releaseRealtimeService() {
  singletonRefCount = Math.max(0, singletonRefCount - 1);
  if (singletonRefCount === 0 && singleton) {
    singleton.disconnect();
  }
}
